import { Link, useLocation } from "wouter";
import { useAuth } from "@/hooks/use-auth";
import { useTown } from "@/hooks/use-town";
import { useState, useRef, useEffect } from "react";
import {
  MapPin, MessageCircle, ChevronLeft, ChevronDown,
  ShoppingBag, Tag, Users, Newspaper, CalendarDays, Beer,
  Building2, Store, Wrench, LifeBuoy, Briefcase,
  Truck, LogOut, Settings, User, Search, Bookmark, Bell, Headset,
} from "lucide-react";
import { TownFilterBar } from "@/components/TownFilterBar";

const PRIMARY_LINKS = [
  { href: "/order",     label: "Order Food",  icon: ShoppingBag },
  { href: "/shop-local", label: "Shop Local", icon: Store },
  { href: "/deals",     label: "Deals",       icon: Tag          },
  { href: "/community", label: "Community",   icon: Users        },
];

function getParentRoute(path: string): string | null {
  const segments = path.split("/").filter(Boolean);
  // Top-level routes (e.g. /deals, /events) have no in-app parent
  if (segments.length < 2) return null;
  // /deals/123 → /deals, /events/slug → /events, etc.
  return "/" + segments[0];
}

const MORE_LINKS = [
  { href: "/news",        label: "Local News",       icon: Newspaper,   desc: "Ayrshire news & updates"           },
  // Two entries, two questions. "What's On" is date-led ("what's on tonight?");
  // "Places" is venue-led ("where's a pub with a pool table?"). These used to be
  // three overlapping entries — What's On, Events and Venues — which was the
  // navigation-level symptom of exactly the blur this rewrite removed.
  { href: "/events",      label: "What's On",         icon: CalendarDays, desc: "Tonight, tomorrow & this weekend" },
  { href: "/places",      label: "Places",            icon: Beer,        desc: "Pubs & clubs by what they've got"  },
  { href: "/businesses",  label: "Businesses",        icon: Building2,   desc: "Trusted local businesses"         },
  { href: "/marketplace", label: "Marketplace",       icon: Store,      desc: "Free items & local for-sale"       },
  { href: "/jobs",        label: "Get Quotes",        icon: Wrench,     desc: "Find a local tradesperson"         },
  { href: "/vacancies",   label: "Find Work",         icon: Briefcase,  desc: "Local job vacancies"               },
  // Two distinct destinations, deliberately worded so neither is mistaken for
  // the other. /support takes a message; /help is a directory of outside
  // helplines and cannot. Labelling the directory "Help & Support" was what
  // made contacting us look possible when it was not.
  { href: "/support",     label: "Contact Support",   icon: Headset,    desc: "Message the Ayrshire Hub team"     },
  { href: "/help",        label: "Local Support",     icon: LifeBuoy,   desc: "Helplines & crisis resources"      },
];

export function Navbar() {
  const { user, logout } = useAuth();
  const { selectedTown } = useTown();
  const [location, navigate] = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const moreRef = useRef<HTMLDivElement>(null);
  const userRef = useRef<HTMLDivElement>(null);

  const orderingJourney = /^\/(?:order(?:\/|$)|my-orders(?:\/|$)|shop-local(?:\/|$))/.test(location);
  const isActive = (href: string) => location.startsWith(href) && href !== "/";
  const initials = user ? user.name.split(" ").map((w) => w[0]).join("").toUpperCase().slice(0, 2) : "";

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (moreRef.current && !moreRef.current.contains(e.target as Node)) setMoreOpen(false);
      if (userRef.current && !userRef.current.contains(e.target as Node)) setUserMenuOpen(false);
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const isBusiness = user && (user.role === "tradesperson" || user.role === "business" || user.role === "admin");

  return (
    <nav className="sticky top-0 z-50 bg-white border-b border-slate-200 shadow-sm">
      <div className="container mx-auto px-4">
        <div className="h-16 flex items-center justify-between gap-1 sm:gap-4">

          {/* Back button (mobile — only shown when a parent route exists) */}
          {(() => {
            const parent = getParentRoute(location);
            return parent ? (
              <button
                className="lg:hidden flex-shrink-0 w-9 h-9 flex items-center justify-center rounded-full hover:bg-slate-100 transition-colors text-slate-600"
                onClick={() => navigate(parent)}
                aria-label="Go back"
              >
                <ChevronLeft size={22} />
              </button>
            ) : (
              <span className="lg:hidden w-9 flex-shrink-0" />
            );
          })()}

          {/* Logo */}
          <Link href="/" className="flex items-center gap-2.5 flex-shrink-0" onClick={() => setMenuOpen(false)}>
            <div className="w-9 h-9 rounded-xl bg-primary flex items-center justify-center shadow-sm">
              <MapPin size={16} className="text-amber-400" />
            </div>
            <div className="flex flex-col leading-none">
              <span className="text-[15px] font-bold text-primary tracking-tight">Ayrshire Hub</span>
              {selectedTown !== "All Towns" && (
                <span className="text-[10px] text-slate-400 font-medium">{selectedTown}</span>
              )}
            </div>
          </Link>

          {/* Primary nav (desktop) */}
          <div className="hidden lg:flex items-center gap-0.5 flex-1 justify-center">
            {PRIMARY_LINKS.map(({ href, label, icon: Icon }) => (
              <Link
                key={href}
                href={href}
                className={`px-3.5 py-2 rounded-lg text-sm font-semibold transition-all flex items-center gap-1.5 ${
                  isActive(href)
                    ? "bg-primary text-white shadow-sm"
                    : "text-slate-600 hover:text-primary hover:bg-primary/8"
                }`}
              >
                <Icon size={14} />
                {label}
              </Link>
            ))}

            {/* More dropdown */}
            <div ref={moreRef} className="relative">
              <button
                onClick={() => setMoreOpen((o) => !o)}
                className={`px-3.5 py-2 rounded-lg text-sm font-semibold transition-all flex items-center gap-1.5 ${
                  moreOpen ? "bg-slate-100 text-slate-800" : "text-slate-600 hover:text-primary hover:bg-primary/8"
                }`}
              >
                More
                <ChevronDown size={14} className={`transition-transform ${moreOpen ? "rotate-180" : ""}`} />
              </button>

              {moreOpen && (
                <div className="absolute top-full left-0 mt-1.5 w-72 bg-white rounded-2xl shadow-xl border border-slate-100 overflow-hidden z-50 py-2">
                  {MORE_LINKS.map(({ href, label, icon: Icon, desc }) => (
                    <Link
                      key={href}
                      href={href}
                      onClick={() => setMoreOpen(false)}
                      className="flex items-center gap-3 px-4 py-2.5 hover:bg-slate-50 transition-colors group"
                    >
                      <div className="w-8 h-8 rounded-lg bg-slate-100 group-hover:bg-primary/10 flex items-center justify-center flex-shrink-0 transition-colors">
                        <Icon size={15} className="text-slate-500 group-hover:text-primary transition-colors" />
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-slate-800 leading-tight">{label}</p>
                        <p className="text-xs text-slate-400 leading-tight mt-0.5">{desc}</p>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Right side (desktop) */}
          <div className="hidden lg:flex items-center gap-2 flex-shrink-0">
            <Link
              href="/search"
              className={`p-2 rounded-lg transition-colors ${isActive("/search") ? "bg-primary/10 text-primary" : "text-slate-500 hover:text-primary hover:bg-slate-100"}`}
              title="Search"
            >
              <Search size={20} />
            </Link>
            {/* Contact support — persistent, on every page, signed in or not.
                The navbar is sticky site-wide, so this is genuinely one click
                from anywhere. It is NOT a floating button because the bottom
                right corner is already owned by OrderTrackerDock and the
                community compose FAB. */}
            <Link
              href="/support"
              className={`p-2 rounded-lg transition-colors ${isActive("/support") ? "bg-primary/10 text-primary" : "text-slate-500 hover:text-primary hover:bg-slate-100"}`}
              title="Contact support"
              aria-label="Contact support"
            >
              <Headset size={20} />
            </Link>
            {user ? (
              <>
                {isBusiness && (
                  <Link href="/business">
                    <button className="text-sm font-semibold text-primary border border-primary/30 bg-primary/5 px-3.5 py-2 rounded-lg hover:bg-primary/10 transition-colors">
                      Portal
                    </button>
                  </Link>
                )}
                {user.role === "admin" && (
                  <Link href="/admin">
                    <button className="text-sm font-semibold text-slate-600 border border-slate-200 px-3 py-2 rounded-lg hover:border-primary hover:text-primary transition-colors">
                      Admin
                    </button>
                  </Link>
                )}
                <Link
                  href="/messages"
                  className={`relative p-2 rounded-lg transition-colors ${isActive("/messages") ? "bg-primary/10 text-primary" : "text-slate-500 hover:text-primary hover:bg-slate-100"}`}
                >
                  <MessageCircle size={20} />
                </Link>
                <Link
                  href="/notifications"
                  className={`p-2 rounded-lg transition-colors ${isActive("/notifications") ? "bg-primary/10 text-primary" : "text-slate-500 hover:text-primary hover:bg-slate-100"}`}
                  aria-label="Notifications"
                >
                  <Bell size={20} />
                </Link>

                {/* User avatar dropdown */}
                <div ref={userRef} className="relative">
                  <button
                    onClick={() => setUserMenuOpen((o) => !o)}
                    className="flex items-center gap-2 pl-2 border-l border-slate-200 hover:opacity-80 transition-opacity ml-1"
                  >
                    <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center">
                      <span className="text-white text-xs font-bold">{initials}</span>
                    </div>
                    <ChevronDown size={14} className={`text-slate-400 transition-transform ${userMenuOpen ? "rotate-180" : ""}`} />
                  </button>

                  {userMenuOpen && (
                    <div className="absolute top-full right-0 mt-1.5 w-52 bg-white rounded-2xl shadow-xl border border-slate-100 overflow-hidden z-50 py-2">
                      <div className="px-4 py-2 border-b border-slate-100 mb-1">
                        <p className="text-sm font-semibold text-slate-800 truncate">{user.name}</p>
                        <p className="text-xs text-slate-400 capitalize">{user.role}</p>
                      </div>
                      {[
                        { href: "/account",   label: "My Account",       icon: User        },
                        { href: "/my-orders", label: "My Orders",        icon: ShoppingBag },
                        { href: "/my-deals",  label: "My Deals",         icon: Tag         },
                        { href: "/my-jobs",   label: "My Quotes",        icon: Wrench      },
                        { href: "/saved",     label: "Saved Items",      icon: Bookmark    },
                      ].map(({ href, label, icon: Icon }) => (
                        <Link key={href} href={href} onClick={() => setUserMenuOpen(false)}
                          className="flex items-center gap-2.5 px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50 transition-colors">
                          <Icon size={14} className="text-slate-400" /> {label}
                        </Link>
                      ))}
                      {user.role === "resident" && (
                        <Link href="/become-tradesperson" onClick={() => setUserMenuOpen(false)}
                          className="flex items-center gap-2.5 px-4 py-2.5 text-sm text-amber-700 hover:bg-amber-50 dark:bg-amber-900/20 transition-colors border-t border-slate-100 mt-1">
                          <Wrench size={14} className="text-amber-500" /> Join as Tradesperson
                        </Link>
                      )}
                      <Link href="/become-a-driver" onClick={() => setUserMenuOpen(false)}
                        className="flex items-center gap-2.5 px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50 transition-colors">
                        <Truck size={14} className="text-slate-400" /> Become a Driver
                      </Link>
                      <button
                        onClick={() => { void logout(); setUserMenuOpen(false); }}
                        className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-red-500 hover:bg-red-50 transition-colors border-t border-slate-100 mt-1"
                      >
                        <LogOut size={14} /> Sign out
                      </button>
                    </div>
                  )}
                </div>
              </>
            ) : (
              <div className="flex items-center gap-2">
                <Link href="/login">
                  <button className="text-sm font-medium text-slate-600 px-3.5 py-2 rounded-lg hover:bg-slate-100 transition-colors">
                    Login
                  </button>
                </Link>
                <Link href="/signup">
                  <button className="text-sm font-bold bg-primary hover:bg-primary/90 text-white px-4 py-2 rounded-lg transition-colors shadow-sm">
                    Sign Up Free
                  </button>
                </Link>
              </div>
            )}
          </div>

          {/* Search + support shortcuts (mobile) */}
          <Link href="/search" className="lg:hidden p-2 rounded-lg text-slate-500 hover:text-primary hover:bg-slate-100 transition-colors" aria-label="Search" onClick={() => setMenuOpen(false)}>
            <Search size={20} />
          </Link>
          <Link
            href="/support"
            className={`lg:hidden p-2 rounded-lg transition-colors ${isActive("/support") ? "bg-primary/10 text-primary" : "text-slate-500 hover:text-primary hover:bg-slate-100"}`}
            aria-label="Contact support"
            onClick={() => setMenuOpen(false)}
          >
            <Headset size={20} />
          </Link>

          {/* Hamburger (mobile) */}
          <button
            className="lg:hidden p-2 rounded-lg text-slate-600 hover:bg-slate-100 transition-colors"
            onClick={() => setMenuOpen((o) => !o)}
            aria-label="Toggle menu"
          >
            <div className="w-5 flex flex-col gap-1.5">
              <span className={`block h-0.5 bg-current rounded transition-all duration-200 ${menuOpen ? "rotate-45 translate-y-2" : ""}`} />
              <span className={`block h-0.5 bg-current rounded transition-all duration-200 ${menuOpen ? "opacity-0" : ""}`} />
              <span className={`block h-0.5 bg-current rounded transition-all duration-200 ${menuOpen ? "-rotate-45 -translate-y-2" : ""}`} />
            </div>
          </button>
        </div>
      </div>

      {/* The active-order banner that used to live here has been replaced by
          the site-wide OrderTrackerDock (mounted in AppLayout). The dock shows
          every active order, not just the first, and surfaces the delivery
          code — which this banner never did. Two competing active-order UIs on
          one screen would be worse than either, so this one is gone. */}

      {!orderingJourney && <TownFilterBar />}

      {/* Mobile menu */}
      {menuOpen && (
        <div className="lg:hidden bg-white border-t border-slate-100 shadow-lg overflow-y-auto max-h-[calc(100svh-64px)]">
          <div className="container mx-auto px-4 py-3">

            {/* Primary actions */}
            <div className="grid grid-cols-2 gap-2 mb-4">
              {PRIMARY_LINKS.map(({ href, label, icon: Icon }) => (
                <Link key={href} href={href} onClick={() => setMenuOpen(false)}>
                  <div className={`flex flex-col items-center gap-1.5 p-3 rounded-xl text-center transition-colors ${isActive(href) ? "bg-primary text-white" : "bg-slate-50 text-slate-700 hover:bg-slate-100"}`}>
                    <Icon size={20} />
                    <span className="text-xs font-semibold leading-tight">{label}</span>
                  </div>
                </Link>
              ))}
            </div>

            {/* Search */}
            <Link
              href="/search"
              onClick={() => setMenuOpen(false)}
              className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors mb-1 ${isActive("/search") ? "bg-primary/10 text-primary" : "text-slate-700 hover:bg-slate-50"}`}
            >
              <Search size={16} className="text-slate-400 flex-shrink-0" />
              Search
            </Link>

            {/* More links */}
            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider px-1 mb-2">More</p>
            <div className="flex flex-col gap-0.5 mb-3">
              {MORE_LINKS.map(({ href, label, icon: Icon }) => (
                <Link
                  key={href}
                  href={href}
                  onClick={() => setMenuOpen(false)}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors ${
                    isActive(href) ? "bg-primary/10 text-primary" : "text-slate-700 hover:bg-slate-50"
                  }`}
                >
                  <Icon size={16} className="text-slate-400 flex-shrink-0" />
                  {label}
                </Link>
              ))}
            </div>

            {/* Account section */}
            <div className="border-t border-slate-100 pt-3">
              {user ? (
                <div className="flex flex-col gap-1">
                  <Link href="/account" onClick={() => setMenuOpen(false)}>
                    <div className="flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-slate-50 transition-colors">
                      <div className="w-9 h-9 rounded-full bg-primary flex items-center justify-center flex-shrink-0">
                        <span className="text-white text-xs font-bold">{initials}</span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-slate-800 truncate">{user.name}</p>
                        <p className="text-xs text-slate-400 capitalize">{user.role}</p>
                      </div>
                      <Settings size={15} className="text-slate-300" />
                    </div>
                  </Link>
                  {user && (
                    <Link href="/messages" onClick={() => setMenuOpen(false)} className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors">
                      <MessageCircle size={16} className="text-slate-400" /> Messages
                    </Link>
                  )}
                  {user && (
                    <Link href="/notifications" onClick={() => setMenuOpen(false)} className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors">
                      <Bell size={16} className="text-slate-400" /> Notifications
                    </Link>
                  )}
                  {isBusiness && (
                    <Link href="/business" onClick={() => setMenuOpen(false)} className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-semibold text-primary hover:bg-primary/5 transition-colors">
                      <Store size={16} className="text-primary" /> Business Portal
                    </Link>
                  )}
                  {user.role === "admin" && (
                    <Link href="/admin" onClick={() => setMenuOpen(false)}>
                      <div className="px-4 py-3 rounded-xl text-sm font-bold text-white bg-primary hover:bg-primary/90 transition-colors text-center mt-1">
                        Admin Dashboard
                      </div>
                    </Link>
                  )}
                  <button
                    onClick={() => { void logout(); setMenuOpen(false); }}
                    className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-red-500 hover:bg-red-50 transition-colors mt-1"
                  >
                    <LogOut size={16} /> Sign out
                  </button>
                </div>
              ) : (
                <div className="flex flex-col gap-2 px-1">
                  <Link href="/become-a-driver" onClick={() => setMenuOpen(false)}>
                    <div className="flex items-center justify-center gap-2 bg-primary/8 border border-primary/20 text-primary font-semibold text-sm py-2.5 rounded-xl hover:bg-primary/12 transition-colors">
                      <Truck size={14} /> Earn with Ayrshire Hub — Become a Driver
                    </div>
                  </Link>
                  <div className="flex gap-2">
                    <Link href="/login" onClick={() => setMenuOpen(false)} className="flex-1">
                      <button className="w-full border border-slate-200 text-slate-700 font-semibold text-sm py-2.5 rounded-xl hover:bg-slate-50 transition-colors">Login</button>
                    </Link>
                    <Link href="/signup" onClick={() => setMenuOpen(false)} className="flex-1">
                      <button className="w-full bg-primary hover:bg-primary/90 text-white font-bold text-sm py-2.5 rounded-xl transition-colors">Sign Up Free</button>
                    </Link>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}
